import type {
  AudioChunk,
  AudioEncodeConfig,
  AudioEncoderAdapter,
  AudioEncoderState,
  EncodedAudioChunkLike,
} from '../../visual/index.js';
import type { NativeAudioChunk } from './AudioBufferPcm.js';

/**
 * `AudioEncoder`, behind the adapter the core holds.
 *
 * The audio twin of `WebCodecsVideoEncoder`, down to the shape of its failure
 * handling: WebCodecs reports encode errors through a callback rather than by
 * throwing, so a broken encode surfaces a turn or two after the `encode()` that
 * caused it. Left alone that produces an export which reports success and
 * contains no sound.
 *
 * ## The decoder config
 *
 * The first chunk arrives with `metadata.decoderConfig`, and for AAC that
 * metadata contains the `AudioSpecificConfig` — a handful of bytes describing
 * the sample rate, channel layout and profile. A muxer that never receives it
 * writes a track no decoder can start, and the file plays video in silence with
 * no error anywhere. So it is captured here and carried on the chunk.
 */

interface EncodedAudioChunkNative {
  readonly type: 'key' | 'delta';
  readonly timestamp: number;
  readonly duration: number | null;
  readonly byteLength: number;
  copyTo(destination: ArrayBufferView): void;
}

interface AudioEncoderNative {
  readonly state: 'unconfigured' | 'configured' | 'closed';
  readonly encodeQueueSize: number;
  configure(config: Record<string, unknown>): void;
  encode(data: unknown): void;
  flush(): Promise<void>;
  close(): void;
}

interface AudioEncoderConstructor {
  new (init: {
    output: (chunk: EncodedAudioChunkNative, metadata?: { decoderConfig?: unknown }) => void;
    error: (error: Error) => void;
  }): AudioEncoderNative;
  isConfigSupported(config: Record<string, unknown>): Promise<{ supported?: boolean }>;
}

function encoderGlobal(): AudioEncoderConstructor | undefined {
  return (globalThis as unknown as { AudioEncoder?: AudioEncoderConstructor }).AudioEncoder;
}

/** Whether this browser can encode audio at all. */
export function audioEncoderAvailable(): boolean {
  return typeof encoderGlobal() === 'function' &&
    typeof (globalThis as { AudioData?: unknown }).AudioData === 'function';
}

/** Asks the browser whether it could encode with this audio configuration. */
export async function probeAudioConfig(config: AudioEncodeConfig): Promise<boolean> {
  const Encoder = encoderGlobal();
  if (Encoder === undefined) return false;
  try {
    const answer = await Encoder.isConfigSupported(toNativeConfig(config));
    return answer.supported === true;
  } catch {
    return false;
  }
}

function toNativeConfig(config: AudioEncodeConfig): Record<string, unknown> {
  return {
    codec: config.codec,
    sampleRate: config.sampleRate,
    numberOfChannels: config.numberOfChannels,
    bitrate: config.bitrate,
  };
}

export interface WebCodecsAudioEncoderOptions {
  readonly onChunk: (chunk: EncodedAudioChunkLike) => void;
  readonly onError?: (message: string) => void;
}

export class WebCodecsAudioEncoder implements AudioEncoderAdapter {
  private encoder: AudioEncoderNative | null = null;
  private failure: string | null = null;
  private closed = false;
  private chunks = 0;
  private bytes = 0;
  private decoderConfig: unknown = undefined;

  constructor(private readonly options: WebCodecsAudioEncoderOptions) {}

  get state(): AudioEncoderState {
    if (this.failure !== null) return 'errored';
    if (this.encoder === null) return 'unconfigured';
    if (this.closed) return 'closed';
    return this.encoder.state === 'configured' ? 'configured' : 'unconfigured';
  }

  get queueSize(): number {
    return this.encoder?.encodeQueueSize ?? 0;
  }

  /** Chunks emitted so far. Zero means the pipeline is still filling. */
  get producedChunks(): number {
    return this.chunks;
  }

  get lastError(): string | null {
    return this.failure;
  }

  async configure(config: AudioEncodeConfig): Promise<void> {
    const Encoder = encoderGlobal();
    if (Encoder === undefined) {
      throw new Error('WebCodecs AudioEncoder is not available in this browser');
    }
    if (typeof (globalThis as { AudioData?: unknown }).AudioData !== 'function') {
      throw new Error('WebCodecs AudioData is not available in this browser');
    }

    const supported = await probeAudioConfig(config);
    if (!supported) {
      throw new Error(
        `codec ${config.codec} is not supported at ${config.sampleRate} Hz / ` +
        `${config.numberOfChannels} channels`,
      );
    }

    this.failure = null;
    this.closed = false;
    this.decoderConfig = undefined;
    this.encoder = new Encoder({
      output: (chunk, metadata) => {
        this.chunks += 1;
        this.bytes += chunk.byteLength;
        if (metadata?.decoderConfig !== undefined) this.decoderConfig = metadata.decoderConfig;
        this.options.onChunk(wrapChunk(chunk, this.decoderConfig));
      },
      error: (error) => {
        this.failure = error.message;
        this.options.onError?.(error.message);
      },
    });
    this.encoder.configure(toNativeConfig(config));
  }

  encode(chunk: AudioChunk): void {
    if (this.failure !== null) throw new Error(this.failure);
    const encoder = this.encoder;
    if (encoder === null) throw new Error('audio encoder is not configured');
    const native = (chunk as NativeAudioChunk).native;
    if (native === undefined) throw new Error('audio chunk did not come from this host');
    encoder.encode(native);
  }

  async flush(): Promise<void> {
    const encoder = this.encoder;
    if (encoder === null || this.closed) return;
    try {
      await encoder.flush();
    } catch (cause) {
      this.failure = cause instanceof Error ? cause.message : String(cause);
    }
  }

  close(): void {
    const encoder = this.encoder;
    this.closed = true;
    if (encoder === null) return;
    try {
      if (encoder.state !== 'closed') encoder.close();
    } catch {
      // Already closed, or closed by the error path.
    }
    this.encoder = null;
  }

  diagnostics(): {
    readonly state: AudioEncoderState;
    readonly queueSize: number;
    readonly chunks: number;
    readonly bytes: number;
    readonly error: string | null;
    readonly hasDecoderConfig: boolean;
  } {
    return {
      state: this.state,
      queueSize: this.queueSize,
      chunks: this.chunks,
      bytes: this.bytes,
      error: this.failure,
      hasDecoderConfig: this.decoderConfig !== undefined,
    };
  }
}

function wrapChunk(chunk: EncodedAudioChunkNative, decoderConfig: unknown): EncodedAudioChunkLike {
  return {
    type: chunk.type,
    timestampMicros: chunk.timestamp,
    durationMicros: chunk.duration,
    byteLength: chunk.byteLength,
    copyTo(destination: Uint8Array): void {
      chunk.copyTo(destination);
    },
    ...(decoderConfig === undefined ? {} : { decoderConfig }),
  };
}
